import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { DisciplineFilePack } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { DisciplineFilesModal } from './enums'
import { useDisciplineFilesStore } from './files/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'

interface DisciplineFilePackActionsOptions {
  pack: ComputedRef<DisciplineFilePack>
}

interface DisciplineFilePackActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManageFiles: ComputedRef<boolean>
  toggle: () => void
  openEdit: () => void
  openDelete: () => void
}

export const useDisciplineFilePackActions = ({ pack }: DisciplineFilePackActionsOptions): DisciplineFilePackActionsReturn => {
  const store = useDisciplineFilesStore()
  const { canManageFiles } = useDisciplinePrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const open = (modal: DisciplineFilesModal): void => {
    isOpen.value = false
    store.openModal(modal, pack.value.id)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManageFiles,
    toggle,
    openEdit: () => open(DisciplineFilesModal.Pack),
    openDelete: () => open(DisciplineFilesModal.DeletePack),
  }
}
